import * as React from 'react'
import { TranslatedTerms } from '../../globals/TranslatedTerms'
import { createEnDaContent, lang, translateContent } from '../../globals/Translations'
import Investor from '../../shared/Investor'
import { SelectInput } from '../atoms/Inputs'
import { ColumnFlexBox } from '../templates/Wrappers'
import { RiskProfileCalculation } from '../../shared/InvestmentKnowledge'
import { CheckUncheckRow, Row } from '../atoms/ProjectCard'
import { NiceNumberWithCurrency } from '../../shared/UIFormatting'


export type IntervalItem = { displayName: string, value: number }

export const interval100 = [{ displayName: "0-10", value: 10 }, { displayName: "11-20", value: 20 }, { displayName: "21-50", value: 50 },
{ displayName: "51-100", value: 75 }, { displayName: ">100", value: 100 }]

export const intervalMedium = [
    { displayName: "0-10.000 dkk", value: 0 },
    { displayName: "10.001 - 20.000 dkk", value: 15000 },
    { displayName: "20.001 - 50.000 dkk", value: 35000 },
    { displayName: "50.001 - 75.000 dkk", value: 66500 },
    { displayName: ">75.000 dkk", value: 75000 }]

export const intervalLarge = [
    { displayName: "0-20.000 dkk", value: 0 },
    { displayName: "20.001 - 50.000 dkk", value: 35000 },
    { displayName: "50.001 - 150.000 dkk", value: 100000 },
    { displayName: "150.001 - 300.000 dkk", value: 225000 },
    { displayName: "300.001 - 750.000 dkk", value: 525000 },
    { displayName: ">750.000 dkk", value: 750000 }]

export const intervalXLarge = [
    { displayName: "0-10.000 dkk", value: 0 },
    { displayName: "10.000 - 20.000 dkk", value: 15000 },
    { displayName: "20.001 - 50.000 dkk", value: 35000 },
    { displayName: "50.001 - 100.000 dkk", value: 75000 },
    { displayName: "100.001 - 200.000 dkk", value: 150000 },
    { displayName: "200.001 - 300.000 dkk", value: 250000 },
    { displayName: "300.001 - 400.000 dkk", value: 350000 },
    { displayName: "400.001 - 500.000 dkk", value: 450000 },
    { displayName: "500.001 - 750.000 dkk", value: 625000 },
    { displayName: "750.001 - 1.000.000 dkk", value: 875000 },
    { displayName: ">1.000.000 dkk", value: 1000000 }]

export const intervalIncome = [
    { displayName: "<300.000 dkk", value: 300000 },
    { displayName: "300.001 - 400.000 dkk", value: 350000 },
    { displayName: "400.001 - 500.000 dkk", value: 450000 },
    { displayName: "500.001 - 600.000 dkk", value: 550000 },
    { displayName: "600.001 - 700.000 dkk", value: 650000 },
    { displayName: "700.001 - 800.000 dkk", value: 750000 },
    { displayName: "800.001 - 1.000.000 dkk", value: 900000 },
    { displayName: "1.000.001 - 1.500.000 dkk", value: 1250000 },
    { displayName: "1.500.001 - 3.000.000 dkk", value: 2250000 },
    { displayName: ">3.000.000 dkk", value: 3000000 }]

export const intervalMonths = ["0-12", "13-24", ">24"]

type InvestorTestElementProps = {
    salary: number | undefined,
    setSalary: React.Dispatch<React.SetStateAction<number | undefined>>,
    interestIncome: number | undefined,
    setInterestIncome: React.Dispatch<React.SetStateAction<number | undefined>>,
    dividendIncome: number | undefined,
    setDividendIncome: React.Dispatch<React.SetStateAction<number | undefined>>,
    realEstateIncome: number | undefined,
    setRealEstateIncome: React.Dispatch<React.SetStateAction<number | undefined>>,
    pensionAssets: number | undefined,
    setPensionAssets: React.Dispatch<React.SetStateAction<number | undefined>>,
    stockAssets: number | undefined,
    setStockAssets: React.Dispatch<React.SetStateAction<number | undefined>>,
    bondAssets: number | undefined,
    setBondAssets: React.Dispatch<React.SetStateAction<number | undefined>>,
    childSupport: number | undefined,
    setChildSupport: React.Dispatch<React.SetStateAction<number | undefined>>,
    mortgage: number | undefined,
    setMortgage: React.Dispatch<React.SetStateAction<number | undefined>>,
    otherLoans: number | undefined,
    setOtherLoans: React.Dispatch<React.SetStateAction<number | undefined>>,
    insurances: number | undefined,
    setInsurances: React.Dispatch<React.SetStateAction<number | undefined>>,
    utilities: number | undefined,
    setUtilities: React.Dispatch<React.SetStateAction<number | undefined>>,
    subscriptionServices: number | undefined,
    setSubscriptionServices: React.Dispatch<React.SetStateAction<number | undefined>>,
    incometaxAndPropertyTax: number | undefined,
    setIncometaxAndPropertyTax: React.Dispatch<React.SetStateAction<number | undefined>>,
}

export const InvestorTestElement: React.FC<InvestorTestElementProps> = ({ salary, setSalary, interestIncome, setInterestIncome, dividendIncome, setDividendIncome,
    realEstateIncome, setRealEstateIncome, pensionAssets, setPensionAssets, stockAssets, setStockAssets, bondAssets, setBondAssets, childSupport, setChildSupport, mortgage, setMortgage, otherLoans,
    setOtherLoans, insurances, setInsurances, utilities, setUtilities, subscriptionServices, setSubscriptionServices, incometaxAndPropertyTax, setIncometaxAndPropertyTax,
}) => {
    return (
        <ColumnFlexBox>
            <h3 style={{ marginTop: 32 }}>{translateContent(createEnDaContent("Net worth and ability to bear losses", "Nettoformue og evne til at bære tab"))}</h3>
            <h4 style={{ marginTop: 0 }}>{translateContent(createEnDaContent("A. Calculation of annual net income", "A.	Beregning af årlig nettoindkomst"))}</h4>
            <SelectInput options={intervalIncome}
                fieldName={translateContent(createEnDaContent("What is your total 'work income' (wages, unemployment benefits, pension payments)?", "Hvad er din samlede 'arbejdsindkomst' (løn, arbejdsløshedsunderstøttelse, pensionsudbetalinger)?"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()} value={salary?.toString()}
                onSelect={(e: string) => setSalary(Number(e))} />
            <SelectInput options={intervalXLarge}
                fieldName={translateContent(createEnDaContent(
                    "What are your total interest earnings (from bank deposits, bonds, etc.)?",
                    "Hvad er dine samlede \"renteindtægter\" (af bankindskud, gældsbreve mv.)"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={interestIncome?.toString()} onSelect={(e: string) => setInterestIncome(Number(e))} />
            <SelectInput options={intervalXLarge}
                fieldName={translateContent(createEnDaContent(
                    "What are your total dividend payments (dividends from stocks, collective investments (such as investment funds) or other equity instruments)?",
                    "Hvad er dine samlede \"udbyttebetalinger\" (udbytter af aktier, kollektive investeringer (eksempelvis investeringsforeninger) eller andre egenkapitalinstrumenter)"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={dividendIncome?.toString()} onSelect={(e: string) => setDividendIncome(Number(e))} />
            <SelectInput options={intervalXLarge}
                fieldName={translateContent(createEnDaContent(
                    "What are your total real estate income (rental income and other income from leasing of real estate (not capital gains from sales))?",
                    "Hvad er dine samlede \"indtægter af fast ejendom\" (lejeindtægter og øvrige indtægter fra udlejning af fast ejendom (ikke kapitalgevinster fra salg))"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={realEstateIncome?.toString()} onSelect={(e: string) => setRealEstateIncome(Number(e))} />

            <h4 style={{ marginTop: 32 }}>{translateContent(createEnDaContent("B. Calculation of your total liquid assets", "B. Beregning af dine samlede likvide aktiver"))}</h4>
            <SelectInput options={intervalXLarge}
                fieldName={translateContent(createEnDaContent(
                    "The sum of savings products that can be converted into cash within a maximum of 30 calendar days",
                    "Summen af opsparingsprodukter, der kan omsættes til kontanter inden for højest 30 kalenderdage"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={pensionAssets?.toString()} onSelect={(e: string) => setPensionAssets(Number(e))} />
            <SelectInput options={intervalXLarge}
                fieldName={translateContent(createEnDaContent(
                    "Financial instruments traded on a regulated market (such as listed stocks or listed bonds)",
                    "Finansielle instrumenter, der handles på et reguleret marked (eksempelvis børsnoterede aktier eller børsnoterede obligationer)"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={stockAssets?.toString()} onSelect={(e: string) => setStockAssets(Number(e))} />
            <SelectInput options={intervalXLarge}
                fieldName={translateContent(createEnDaContent(
                    "Equity investments in collective investment schemes that provide redemption rights at least once a week (such as investment funds)",
                    "Kapitalandele i kollektive investeringsordninger, der giver genindløsningsrettigheder mindst en gang om ugen (eksempelvis investeringsforeninger)"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={bondAssets?.toString()} onSelect={(e: string) => setBondAssets(Number(e))} />
            <h4 style={{ marginTop: 32 }}>{translateContent(createEnDaContent("C. Calculation of your annual financial obligations", "C. Beregning af dine årlige finansielle forpligtelser"))}</h4>
            <SelectInput options={intervalMedium}
                fieldName={translateContent(createEnDaContent(
                    "How much do you pay annually in maintenance and child support?",
                    "Hvor meget betaler du årligt i underholdsbidrag og børnebidrag"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={childSupport?.toString()} onSelect={(e: string) => setChildSupport(Number(e))} />
            <SelectInput options={intervalLarge}
                fieldName={translateContent(createEnDaContent(
                    "How much do you pay annually in rent and payment of mortgage loans?",
                    "Hvor meget betaler du årligt i husleje og betaling af realkreditlån"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={mortgage?.toString()} onSelect={(e: string) => setMortgage(Number(e))} />
            <SelectInput options={intervalLarge}
                fieldName={translateContent(createEnDaContent(
                    "How much do you repay annually on loans?",
                    "Hvor meget betaler du årligt tilbage på lån"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={otherLoans?.toString()} onSelect={(e: string) => setOtherLoans(Number(e))} />
            <SelectInput options={intervalLarge}
                fieldName={translateContent(createEnDaContent(
                    "How much do you pay annually in insurance premiums?",
                    "Hvor meget betaler du årligt i forsikringspræmier"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={insurances?.toString()} onSelect={(e: string) => setInsurances(Number(e))} />
            <SelectInput options={intervalMedium}
                fieldName={translateContent(createEnDaContent(
                    "How much do you pay annually in utility expenses (electricity, heat, water)?",
                    "Hvor meget betaler du årligt i forsyningsudgifter (el, varme, vand)"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={utilities?.toString()} onSelect={(e: string) => setUtilities(Number(e))} />

            <SelectInput options={intervalMedium}
                fieldName={translateContent(createEnDaContent(
                    "How much do you pay annually in subscriptions to services?",
                    "Hvor meget betaler du årligt i abonnementer på tjenester"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={subscriptionServices?.toString()} onSelect={(e: string) => setSubscriptionServices(Number(e))} />
            <SelectInput options={intervalXLarge}
                fieldName={translateContent(createEnDaContent(
                    "How much do you pay annually in income tax and property taxes?",
                    "Hvor meget betaler du årligt i indkomstskat og ejendomsskatter?"))}
                itemRender={(e: IntervalItem) => lang(e.displayName)} getValue={(e: IntervalItem) => e.value.toString()}
                value={incometaxAndPropertyTax?.toString()} onSelect={(e: string) => setIncometaxAndPropertyTax(Number(e))} />

        </ColumnFlexBox>
    )
}

export enum CalculationResultsStep { ASSETS = "ASSETS", KNOWLEDGE_TEST = "KNOWLEDGE_TEST" }
type InvestorTestCalculationResultProps = { calculations: RiskProfileCalculation, ignoreExperienceAndEducation?: boolean, riskProfileString?: string, step: CalculationResultsStep }
export const InvestorTestCalculationResult: React.FC<InvestorTestCalculationResultProps> = ({ calculations: calculations, ignoreExperienceAndEducation, riskProfileString, step }) => {
    const { assets, netWorth, income, expenses, lossAbility, newWorthMinTreshold, lossAbilityMinTreshold, stocksMinExperience, crowdinvestmentMinExperience, educationalLevelThreshold } = calculations

    return (
        <>
            {step === CalculationResultsStep.ASSETS && <>
                <h4>Beregning af nettoformue</h4>
                {Row(translateContent(createEnDaContent("Assets", "Formue")), NiceNumberWithCurrency(assets), 0)}
                {Row(translateContent(createEnDaContent("Income", "Indkomst(er)")), NiceNumberWithCurrency(income), 1)}
                {Row(translateContent(createEnDaContent("Expenses", "Udgifter")), NiceNumberWithCurrency(expenses), 2)}
                {Row(translateContent(createEnDaContent("Net worth", "Nettoformue")), NiceNumberWithCurrency(netWorth), 3)}
                {Row(translateContent(createEnDaContent("Capacity for losses (10% net worth)", "Evne til at bære tab (10% nettoformue)")), <b>{NiceNumberWithCurrency(lossAbility)}</b>, 4)}
                {CheckUncheckRow(translateContent(createEnDaContent("Net worth min 100.000 dkk", "Nettoformue mindst 100.000 dkk")), newWorthMinTreshold, 5)}
                {CheckUncheckRow(translateContent(createEnDaContent("Capacity for losses min 10.000 kr.", "Evne til at bære tab mindst 10.000 kr.")), lossAbilityMinTreshold, 6)}
            </>}
            {step === CalculationResultsStep.KNOWLEDGE_TEST && <>

                <h4 style={{ marginTop: 32 }}>Resultat af videnstest</h4>
                {!ignoreExperienceAndEducation && CheckUncheckRow(translateContent(createEnDaContent("Investment experience from at least 20 investments in securities over the past 5 years.", "Investeringserfaring fra mindst 20 investering i værdipapirer i løbet af de seneste 5 år")), stocksMinExperience, 2)}
                {!ignoreExperienceAndEducation && CheckUncheckRow(translateContent(createEnDaContent("Investment experience from at least 5 investments in instruments approved for crowdfunding over the past 5 years.", " Investeringserfaring fra mindst 5 investeringer i instrumenter godkendt til crowdfunding i løbet af de seneste 5 år")), crowdinvestmentMinExperience, 3)}
                {!ignoreExperienceAndEducation && CheckUncheckRow(translateContent(createEnDaContent("Education level at least completed 10th grade", "Uddannelsesniveau mindst gennemført 10. klasse")), educationalLevelThreshold, 4)}
                {riskProfileString && Row(translateContent(createEnDaContent("Risk profile", "Risikoprofil")), lang(riskProfileString), 5)}
                <p style={{marginTop: 32}}>{lang(TranslatedTerms.RISK_WARNING_LESS_EXPERIENCEDP1)}</p>
                <p>{lang(TranslatedTerms.RISK_WARNING_LESS_EXPERIENCEDP2)}<a href='https://www.fundbricks.com/legal/risk'>https://www.fundbricks.com/legal/risk</a></p>
                <p>{lang(TranslatedTerms.RISK_WARNING_LESS_EXPERIENCEDP3)}<a href='mailto:hello@fundbricks.com'>hello@fundbricks.com</a></p>
            </>}

        </>
    )
}
